import React, { useState, useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal';
import {
    Button, Text, Grid, Link, Box, Image
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import SmsOptInModalEN from 'components/SmsOptInModal/locales/SmsOptInModal_en_US';
import SmsOptInModalFR from 'components/SmsOptInModal/locales/SmsOptInModal_fr_CA';
import MobilePhoneInput from 'components/Inputs/MobilePhoneInput/MobilePhoneInput';

import FormValidator from 'utils/FormValidator';
import Empty from 'constants/empty';

import bccEmailSMSOptInService from 'services/api/bccEmailSmsOptIn';
import InputMsg from 'components/Inputs/InputMsg/InputMsg';

import urlUtils from 'utils/Url';

const SmsOptInModal = ({
    isOpen, onDismiss, onContinue, smsTextOptInPromos, isUserOptedIn
}) => {
    const [processingSubmission, setProcessingSubmission] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showError, setShowError] = useState(false);
    const [validationError, setValidationError] = useState(true); // Initially disabled with empty phone

    const getText = localeUtils.getLocalisedDataFromResource(SmsOptInModalEN, SmsOptInModalFR);

    const mobileInput = useRef(null);

    useEffect(() => {
        setShowError(false);
    }, []);

    // Check if it's an event object or direct value
    const handlePhoneChange = valueOrEvent => {
        const value = valueOrEvent?.target?.value ?? valueOrEvent;
        setPhoneNumber(value);

        const formattedPhone = FormValidator.getFormattedPhoneNumber(value);

        // Validate on change to update button state - use boolean
        const hasError = FormValidator.isEmpty(value) || formattedPhone.length !== FormValidator.FIELD_LENGTHS.formattedPhone;
        setValidationError(hasError);
    };

    const maskPhoneNumber = phone => {
        // Extract last 4 digits
        const cleaned = phone.replace(/\D/g, '');
        const lastFour = cleaned.slice(-4);

        return `(**) *** - ${lastFour}`;
    };

    const isValid = () => {
        // Trigger validation on the MobilePhoneInput component
        const error = mobileInput.current?.validateError();

        return !error;
    };

    const subscribeUser = async () => {
        setShowError(false);

        if (!isValid()) {
            return;
        }

        setProcessingSubmission(true);

        try {
            const pageName = digitalData?.page?.pageInfo?.pageName || Empty.String();
            const currentLocale = localeUtils.getCurrentLanguageCountryCode();

            await bccEmailSMSOptInService.submitEmailSMSOptInForm({
                phoneNumber,
                pageName: pageName,
                locale: currentLocale,
                pageType: 'SMS_optin',
                requestOrigin: null,
                type: 'SMS'
            });

            setShowConfirmation(true);
        } catch (err) {
            setShowError(true);

            // eslint-disable-next-line no-console
            Sephora.logger.error('Error when subscribing to SMS Text Messages: ', { err });
        } finally {
            setProcessingSubmission(false);
        }
    };

    const handleContinue = () => {
        subscribeUser();
    };

    const handleContinueShopping = () => {
        if (onContinue) {
            onContinue(phoneNumber);
        }

        if (onDismiss) {
            onDismiss();
        }

        // Reset state
        setPhoneNumber('');
        setShowConfirmation(false);
        setValidationError(true);

        // Reloads the page to apply the SMS opt-in status.
        urlUtils.redirectTo(window.location.pathname);
    };

    const handleCancel = () => {
        setShowError(false);
        setPhoneNumber('');
        setShowConfirmation(false);
        setProcessingSubmission(false);
        setValidationError(true);

        if (onDismiss) {
            onDismiss();
        }
    };

    const isContinueDisabled = () => {
        // Use validation error state instead of calling validateError()
        return validationError || processingSubmission;
    };

    const shouldOpenModal = () => {
        const shouldShowSmsTextsOptInModal =
            typeof Sephora !== 'undefined' ? Sephora.configurationSettings?.sms?.orderConfirmationOptInModal?.isEnabled || false : false;

        return shouldShowSmsTextsOptInModal && !isUserOptedIn;
    };

    const getModalImageSrc = () => {
        // Fallback Image.
        const fallBackModalImageSrc = '/img/ufe/sms-optin-get-sephora-texts.png';

        if (smsTextOptInPromos && smsTextOptInPromos.promos && smsTextOptInPromos.promos.length > 0) {
            const latestPromo = smsTextOptInPromos.promos[0] || ''; // Assuming the latest promo is the first one in the array

            return latestPromo !== '' ? latestPromo.promoImgSrc : fallBackModalImageSrc; // Use the promo image if available
        }

        return fallBackModalImageSrc;
    };

    const getModalTitle = () => {
        // Fallback Title.
        const fallBackModalTitle = Empty.String;

        if (smsTextOptInPromos && smsTextOptInPromos.promos && smsTextOptInPromos.promos.length > 0) {
            const latestPromo = smsTextOptInPromos.promos[0] || ''; // Assuming the latest promo is the first one in the array

            return latestPromo !== '' ? latestPromo.promoTitle : fallBackModalTitle; // Use the promo title if available
        }

        return fallBackModalTitle;
    };

    const getModalLabel = () => {
        // Fallback Label.
        const fallBackModalLabel = getText('description');

        if (smsTextOptInPromos && smsTextOptInPromos.promos && smsTextOptInPromos.promos.length > 0) {
            const latestPromo = smsTextOptInPromos.promos[0] || ''; // Assuming the latest promo is the first one in the array

            return latestPromo !== '' ? latestPromo.promoLabel : fallBackModalLabel; // Use the promo label if available
        }

        return fallBackModalLabel;
    };

    const getModalPromoTnC = () => {
        // Fallback Label.
        const fallBackModalPromoTnC = '';

        if (smsTextOptInPromos && smsTextOptInPromos.promos && smsTextOptInPromos.promos.length > 0) {
            const latestPromo = smsTextOptInPromos.promos[0] || ''; // Assuming the latest promo is the first one in the array

            return latestPromo !== '' ? latestPromo.promoTermsAndConditions : fallBackModalPromoTnC; // Use the promo label if available
        }

        return fallBackModalPromoTnC;
    };

    return !shouldOpenModal() ? null : showConfirmation ? (
        <Modal
            isOpen={isOpen}
            onDismiss={handleCancel}
            maxWidth={['100%', '100%', 600]}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title>{getText('confirmationTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body paddingBottom={[5, 5, 6]}>
                <Text
                    is='p'
                    fontSize='md'
                    marginBottom={2}
                    fontWeight='bold'
                    lineHeight='normal'
                >
                    {getText('confirmationMessage')}
                </Text>

                <Text
                    is='p'
                    fontSize='md'
                    marginBottom={4}
                    lineHeight='normal'
                >
                    {getText('confirmationPhonePrefix')} {maskPhoneNumber(phoneNumber)}
                </Text>

                <Button
                    variant='primary'
                    width='100%'
                    onClick={handleContinueShopping}
                    data-at={Sephora.debug.dataAt('sms_optin_continue_shopping_button')}
                >
                    {getText('continueShoppingButton')}
                </Button>
            </Modal.Body>
        </Modal>
    ) : (
        <Modal
            isOpen={isOpen}
            onDismiss={handleCancel}
            maxWidth={['100%', '100%', 600]}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title>{getText('title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body paddingBottom={[5, 5, 6]}>
                <Box
                    borderRadius={2}
                    marginBottom={4}
                    textAlign='center'
                    minHeight={200}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                >
                    <Image
                        src={getModalImageSrc()}
                        width='100%'
                        height='100%'
                        objectFit='cover'
                    />
                </Box>

                <Text
                    is='p'
                    fontSize='md'
                    fontWeight='bold'
                    marginBottom={4}
                    lineHeight='normal'
                >
                    {getModalTitle()}
                </Text>

                <Text
                    is='p'
                    fontSize='md'
                    marginBottom={4}
                    lineHeight='normal'
                >
                    {getModalLabel()}
                </Text>

                <MobilePhoneInput
                    ref={mobileInput}
                    name='mobilePhone'
                    hideAsterisk={true}
                    required={true}
                    autoCorrect='off'
                    label={getText('phonePlaceholder')}
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    dataAtError={Sephora.debug.dataAt('error_msg')}
                    marginBottom={4}
                    validate={value => {
                        if (FormValidator.isEmpty(value)) {
                            return getText('emptyError');
                        }

                        if (value.length !== FormValidator.FIELD_LENGTHS.formattedPhone) {
                            return getText('invalidError');
                        }

                        return null;
                    }}
                />

                <Text
                    is='p'
                    fontSize='xs'
                    color='gray'
                    lineHeight='tight'
                    marginBottom={1}
                >
                    {getText('disclaimer1')}{' '}
                    <Link
                        href='/beauty/sms-terms-and-conditions'
                        target='_blank'
                        color='blue'
                        underline={true}
                        data-at={Sephora.debug.dataAt('sms_optin_text_terms_link')}
                    >
                        {getText('textTerms')}
                    </Link>
                    . {getText('disclaimer2')}{' '}
                    <Link
                        href='/beauty/privacy-policy'
                        target='_blank'
                        color='blue'
                        underline={true}
                        data-at={Sephora.debug.dataAt('sms_optin_privacy_link')}
                    >
                        {getText('privacyPolicy')}
                    </Link>{' '}
                    {getText('and')}{' '}
                    <Link
                        href='/beauty/privacy-policy#USNoticeIncentive'
                        target='_blank'
                        color='blue'
                        underline={true}
                        data-at={Sephora.debug.dataAt('sms_optin_financial_link')}
                    >
                        {getText('noticeOfFinancialIncentive')}
                    </Link>{' '}
                    {getText('cancelHelp')}
                </Text>

                <Text
                    is='p'
                    fontSize='xs'
                    color='black'
                    lineHeight='tight'
                    marginTop={4}
                    marginBottom={1}
                >
                    {getModalPromoTnC()}
                </Text>
                {showError && <InputMsg color={'error'}>{getText('errorSubmitting')}</InputMsg>}
                <Grid
                    columns={[1, 1, 2]}
                    gap={3}
                    marginTop={4}
                >
                    <Button
                        variant='secondary'
                        width='100%'
                        onClick={handleCancel}
                        data-at={Sephora.debug.dataAt('sms_optin_cancel_button')}
                    >
                        {getText('cancelButton')}
                    </Button>
                    <Button
                        variant='primary'
                        width='100%'
                        onClick={handleContinue}
                        disabled={isContinueDisabled()}
                        data-at={Sephora.debug.dataAt('sms_optin_continue_button')}
                    >
                        {getText('continueButton')}
                    </Button>
                </Grid>
            </Modal.Body>
        </Modal>
    );
};

export default wrapFunctionalComponent(SmsOptInModal, 'SmsOptInModal');
